import * as React from 'react';
import Icons from '@/const/icons';
import { Spinner, Typography } from '@/components/common';
import { ActionRequiredCardProps } from '@/interfaces';
import { useActionRequiredActions } from '@/hooks/useActionRequiredActions';
import { useActionRequiredOnboarding } from '@/hooks/useActionRequiredOnboarding';
import { GuidedTourModal } from '@/components/modals/guided-tour/GuidedTourModal';
import { sortByDueDate } from '@/utils/actionRequired';
import { isCaseCardDisabled } from '@/utils/caseStatus';
import { ActionRequiredModals } from '../action-required/ActionRequiredModals';
import { ActionRequiredWelcomeModal } from '../action-required/ActionRequiredWelcomeModal';
import { CaseDetailsCard } from './CaseDetailsCard';
import { CaseDetailsEmptyState } from './CaseDetailsEmptyState';
import { CaseDetailsErrorState } from './CaseDetailsErrorState';
import { CasePill } from './CasePill';
import {
	ActionRequiredRow,
	TakeTheTourRow,
	VerifyIdentityRow,
} from './ActionRequiredRow';

export const ActionRequiredCard: React.FC<ActionRequiredCardProps> = ({
	actionRequired,
	applicationId,
	caseName,
	status,
	stepName,
	numericalOrder,
	clientApproveEmail,
	className,
	refetchActionRequired,
	refetchCase,
	refetchDocuments,
	onViewCaseOverview,
	progress,
	isDashboard = false,
	isError = false,
	onRetry,
}) => {
	const actions = useActionRequiredActions({
		actionRequired,
		numericalOrder,
		refetchers: { refetchActionRequired, refetchCase, refetchDocuments },
	});
	const onboarding = useActionRequiredOnboarding({ isDashboard, actions });
	const {
		actionRequiredList,
		visibleActionRequiredList,
		showVerifyIdentityAction,
		handleClickOptionToOpen,
		selectActionRequired,
		openStandaloneVerification,
		hasDocumentAction,
	} = actions;
	const { showTakeTheTourAction } = onboarding;

	const pendingCount =
		visibleActionRequiredList.length + (showVerifyIdentityAction ? 1 : 0);
	const hasRows = pendingCount > 0 || showTakeTheTourAction;
	const hasLoadError = !actionRequiredList && isError && !!onRetry;
	const isLoading = !actionRequiredList && !hasLoadError;
	const hasNoList = !actionRequiredList;

	return (
		<>
			<CaseDetailsCard
				title="Action Required"
				pill={
					hasNoList ? undefined : (
						<CasePill tone={pendingCount > 0 ? 'danger' : 'neutral'}>
							{pendingCount} pending
						</CasePill>
					)
				}
				accent={hasNoList ? 'none' : pendingCount > 0 ? 'danger' : 'success'}
				disabled={
					!!isCaseCardDisabled({ status, stepName, clientApproveEmail })
				}
				className={className}
				classNameBody={progress ? '!pb-0' : undefined}
			>
				{isLoading ? (
					<div className="flex w-full flex-1 items-center justify-center py-10">
						<Spinner type="loadingPage" />
					</div>
				) : hasLoadError && onRetry ? (
					<CaseDetailsErrorState
						className="flex-1"
						message="We couldn’t load your pending actions. Please try again."
						onRetry={onRetry}
					/>
				) : hasRows ? (
					<>
						<Typography type="regular-13" className="mt-3 text-gray-35">
							Complete these tasks to keep your case moving forward.
						</Typography>
						<ul
							role="list"
							className="scroll-custom mt-4 min-h-0 flex-1 overflow-y-auto rounded-2xl border border-grey-100"
						>
							{showVerifyIdentityAction && (
								<VerifyIdentityRow onVerify={openStandaloneVerification} />
							)}
							{showTakeTheTourAction && (
								<TakeTheTourRow onStart={onboarding.handleTakeTheTourClick} />
							)}
							{sortByDueDate(visibleActionRequiredList).map((item) => (
								<ActionRequiredRow
									key={item.id}
									item={item}
									showCaseName={isDashboard}
									onOpen={handleClickOptionToOpen}
									onSelect={selectActionRequired}
								/>
							))}
						</ul>
					</>
				) : (
					<CaseDetailsEmptyState
						className="mt-[14px] flex-1 !px-4 lg:mt-4 lg:!px-6"
						icon={Icons.briefcase}
						iconClassName="text-gray-35"
						iconSize="md"
						titleType="bold-24"
						title={
							isDashboard
								? 'Your cases are moving forward'
								: 'Your case is moving forward'
						}
						text={
							<>
								Your application is in review. We’ll let you know as soon as{' '}
								<br className="hidden lg:block" />
								we need anything from you.
							</>
						}
						action={
							onViewCaseOverview && {
								label: 'View Case Overview',
								onClick: onViewCaseOverview,
							}
						}
					/>
				)}
				{progress && (
					<div className="-mx-[18px] mt-auto shrink-0 pt-6 lg:-mx-7 lg:pt-7">
						{progress}
					</div>
				)}
			</CaseDetailsCard>

			<ActionRequiredModals
				actions={actions}
				applicationId={applicationId}
				caseName={caseName}
			/>

			{isDashboard && (
				<ActionRequiredWelcomeModal
					Modal={onboarding.WelcomeModal}
					hasDocumentAction={hasDocumentAction}
					onDismiss={onboarding.dismissWelcome}
					onStartVerification={onboarding.handleStartVerificationFromWelcome}
				/>
			)}

			{showTakeTheTourAction && (
				<GuidedTourModal
					Modal={onboarding.ModalGuidedTour}
					onClose={onboarding.hideGuidedTour}
					onFinish={onboarding.hideGuidedTour}
				/>
			)}
		</>
	);
};
